// ---------------------------------------------------------------------------
// True only once something has been true for a while.
//
// The reported case is a phone. Going to the home screen and coming back drops
// the socket and brings it straight back, and the reconnecting notice flashed
// up for about a quarter of a second on the way through — motion in the corner
// of the eye reporting a problem that had already fixed itself before it was
// read.
//
// **The connection state itself is not debounced, and must not be.** The store
// says the socket is down the moment it is down, because things that are not
// the eye depend on that being true: a draft in flight is genuinely at risk
// from the first millisecond, and `ConnectionStatus` still reports that case
// with no delay at all. What is held back is the *telling* of the ordinary
// case, which is a different question with a different right answer.
//
// **No clock is read here, and no elapsed time is measured.** The first two
// attempts at this file did both and neither survived the lint: a `setHeld`
// reset in the effect is a setState inside an effect, and a ref holding the
// start time is a ref read during render. Both are refused by the React
// Compiler rules this project builds under, and both were solving a problem
// that does not need solving — the timer already knows when the grace period is
// up, so nothing has to count. What remains is the documented
// adjust-state-during-render pattern, which resets the answer in the same
// render that flips `active` rather than a frame later.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';

/**
 * How long a socket has to stay down before the player is told about it.
 *
 * A backgrounded tab reconnects in well under this. A real drop does not, and
 * the second it costs is a second in which nothing was worth saying anyway.
 * Sibling of `SAVE_GRACE_MS` in `ConnectionStatus.tsx`, which holds back the
 * saving indicator on exactly the same reasoning.
 */
export const DISCONNECT_GRACE_MS = 1000;

export function useHeldTrue(active: boolean, ms: number): boolean {
  const [tracking, setTracking] = useState(active);
  const [held, setHeld] = useState(false);

  // React's own "adjusting state when a prop changes" pattern: the reset lands
  // in the render that flips `active`, so a connection coming back never shows
  // even one frame of the notice on its way out.
  if (tracking !== active) {
    setTracking(active);
    setHeld(false);
  }

  useEffect(() => {
    if (!active) return;
    // Fires once, at the moment the answer changes. Nothing repaints in
    // between, so a socket that stays down costs one wake-up rather than a
    // running interval.
    const timer = window.setTimeout(() => setHeld(true), ms);
    return () => window.clearTimeout(timer);
  }, [active, ms]);

  return active && held;
}
